import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Volume2, Loader2, Check } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { id: "en", label: "English" },
  { id: "hi", label: "हिन्दी" },
  { id: "hinglish", label: "Hinglish" },
];

// Three steps only · this opens on the athlete's phone, so it has to be over in a minute.
const STEPS = [
  { to: "/athlete", target: "today-bar",
    en: { t: "Your numbers, every morning", b: "Readiness = ready to train. Rest = how well you recovered. Effort = how hard you worked. Sharpness = how sharp your skill is. Higher is better." },
    hi: { t: "हर सुबह आपके नंबर", b: "Readiness = ट्रेनिंग के लिए तैयार। Rest = रिकवरी कैसी हुई। Effort = कितना मेहनत की। Sharpness = स्किल कितनी शार्प है। ज़्यादा = अच्छा।" },
    hinglish: { t: "Har subah aapke number", b: "Readiness = training ke liye taiyaar. Rest = recovery kaisi hui. Effort = kitni mehnat ki. Sharpness = skill kitni sharp hai. Zyada = accha." } },
  { to: "/athlete?tab=performance", target: "trifit-connect",
    en: { t: "Connect your Trifit app once", b: "Put the same email you use in the Trifit app and tap Connect. Your sleep, steps, workouts and food come in on their own after that." },
    hi: { t: "एक बार Trifit ऐप जोड़िए", b: "जो ईमेल आप Trifit ऐप में इस्तेमाल करते हैं वही डालिए और Connect दबाइए। उसके बाद नींद, स्टेप्स, वर्कआउट और खाना अपने आप आ जाता है।" },
    hinglish: { t: "Ek baar Trifit app connect kijiye", b: "Jo email aap Trifit app me use karte hain wahi daaliye aur Connect dabaiye. Uske baad neend, steps, workout aur khaana khud aa jaata hai." } },
  { to: "/athlete?tab=plan", target: "add-task-day",
    en: { t: "Today's plan lives here", b: "Your coach writes the session here and it ticks off on its own from your watch. Anything unclear — tap the chat button and ask in your own language." },
    hi: { t: "आज का प्लान यहाँ है", b: "कोच यहाँ सेशन लिखते हैं और आपकी घड़ी से वो अपने आप टिक हो जाता है। कुछ समझ न आए — चैट बटन दबाकर अपनी भाषा में पूछिए।" },
    hinglish: { t: "Aaj ka plan yahan hai", b: "Coach yahan session likhte hain aur aapki watch se wo khud tick ho jaata hai. Kuch samajh na aaye — chat button dabakar apni bhasha me poochiye." } },
];

const KEY = "xps.tour.lang";

export default function AthleteTour({ open, onClose }) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [lang, setLang] = useState(() => localStorage.getItem(KEY) || "en");
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const step = STEPS[i];
  const copy = step?.[lang] || step?.en;

  useEffect(() => { localStorage.setItem(KEY, lang); }, [lang]);

  useEffect(() => {
    if (!open || !step) return;
    navigate(step.to);
    const id = setTimeout(() => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("tour-spot");
      setTimeout(() => el.classList.remove("tour-spot"), 3200);
    }, 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, i, lang]);

  useEffect(() => () => { audioRef.current?.pause?.(); }, []);
  if (!open || !step) return null;

  const speak = async () => {
    setSpeaking(true);
    try {
      const { data } = await api.post("/tts", { text: `${copy.t}. ${copy.b}`, voice: "nova" });
      audioRef.current?.pause?.();
      const a = new Audio(`${API_BASE}${data.url}`);
      audioRef.current = a;
      a.onended = () => setSpeaking(false);
      a.onerror = () => setSpeaking(false);
      await a.play();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice is unavailable right now");
      setSpeaking(false);
    }
  };

  const finish = async () => {
    audioRef.current?.pause?.();
    try { await api.post("/me/tutorial-complete"); } catch { /* ignore */ }
    onClose?.();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] p-3 pointer-events-none" data-testid="athlete-tour">
      <div className="pointer-events-auto mx-auto w-full max-w-xl bg-background border-2 border-foreground shadow-2xl">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 flex-wrap">
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">Step {i + 1} of {STEPS.length}</span>
          <div className="flex-1 flex gap-1 min-w-[60px]">
            {STEPS.map((_, k) => <span key={k} className={`h-1 flex-1 ${k <= i ? "bg-foreground" : "bg-border"}`} />)}
          </div>
          <div className="inline-flex border border-border overflow-hidden" data-testid="athlete-tour-lang">
            {LANGS.map((l) => (
              <button key={l.id} data-testid={`athlete-tour-lang-${l.id}`} onClick={() => setLang(l.id)}
                className={`px-2 py-1 text-[11px] transition-colors ${lang === l.id ? "bg-foreground text-background" : "hover:bg-surface"}`}>
                {l.label}
              </button>
            ))}
          </div>
          <button data-testid="athlete-tour-close" onClick={finish} aria-label="Close" className="p-1 hover:bg-surface transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4">
          <h3 className="font-heading font-black tracking-tight text-base md:text-lg">{copy.t}</h3>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{copy.b}</p>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <button data-testid="athlete-tour-speak" onClick={speak} disabled={speaking}
              className="flex items-center gap-1.5 text-xs border border-border px-3 py-2 hover:bg-surface transition-colors disabled:opacity-50">
              {speaking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />} Listen
            </button>
            {i > 0 && (
              <button data-testid="athlete-tour-back" onClick={() => setI((v) => v - 1)}
                className="flex items-center gap-1.5 text-xs border border-border px-3 py-2 hover:bg-surface transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {i < STEPS.length - 1 ? (
              <button data-testid="athlete-tour-next" onClick={() => setI((v) => v + 1)}
                className="ml-auto flex items-center gap-1.5 text-sm bg-foreground text-background px-4 py-2 font-bold hover:bg-neutral-800 transition-colors">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button data-testid="athlete-tour-done" onClick={finish}
                className="ml-auto flex items-center gap-1.5 text-sm bg-foreground text-background px-4 py-2 font-bold hover:bg-neutral-800 transition-colors">
                <Check className="w-4 h-4" /> Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
